import { Formik, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useGetVideosQuery } from '../../features/videos/videosApi';
import { customStyles } from '../../utils/constantVariables';
import { useAddQuizzMutation, useEditQuizzMutation } from '../../features/quizzes/quizzesApi';
import { toggleQuizzModal } from '../../features/quizzes/quizzesSlice';


Modal.setAppElement('#root');



const quizzSchema = Yup.object().shape({
	question: Yup.string().required('Question is required'),
	video_id: Yup.string().required('Video Title is required'),
	options: Yup.array()
		.of(
			Yup.object().shape({
				id: Yup.string(),
				option: Yup.string().required('Option text is required'),
				isCorrect: Yup.boolean(),
			})
		)
		.required('At least one option is required')
		.test('at-least-one-true', 'At least one option must be true', function (options) {
			return options.some((option) => option.isCorrect);
		}),
});

function QuizzForm() {

	const defaultOption = {
		id: '',
		option: '',
		isCorrect: false,
	};
	const { isQuizzModalOpen, quizzFormData } = useSelector((state) => state.quizzes);
	const { id = '', question = '', video_id = '', video_title = '', options = [] } = quizzFormData || {};
	const formattedOptions = Array.from({ length: 4 }, (_, index) => ({
		...defaultOption,
		...options[index],
		id: options[index]?.id || index + 1,
	}));

	const dispatch = useDispatch();
	const [addQuizz, { isSuccess: addSuccess, isError: addError, isLoading: editLoading }] = useAddQuizzMutation();
	const [editQuizz, { isSuccess: editSuccess, isError: editError, isLoading: addLoading }] = useEditQuizzMutation();
	const videosQuery = useGetVideosQuery();
	const videos = videosQuery.data ?? [];
	const initialValues = {
		id,
		question,
		video_id,
		video_title,
		options: formattedOptions,
	};


	//Hooks
	useEffect(() => {
		if (addSuccess || editSuccess) {
			dispatch(toggleQuizzModal(false));
		}
	}, [addSuccess, editSuccess, dispatch])

	const { values, handleBlur, handleChange, handleSubmit, touched, errors } = useFormik({
		initialValues,
		validationSchema: quizzSchema,
		onSubmit: async (values, { resetForm }) => {
			const payload = { ...values }
			payload.video_id = Number(payload.video_id);
			const { title } = videos.find(video => video.id === payload.video_id);
			payload.video_title = title;
			if (id) {
				editQuizz({ id: id, data: payload })
			} else {
				addQuizz({ data: payload });
			}
		},
	});

	//Functions
	const handleModal = () => {
		dispatch(toggleQuizzModal(!isQuizzModalOpen));
	}

	return (
		<div>
			<Modal
				isOpen={isQuizzModalOpen}
				onRequestClose={handleModal}
				style={customStyles}
			>
				<form onSubmit={handleSubmit}>
					<div>
						<label htmlFor="name">Question:</label>
						<input
							type="text"
							id="question"
							name="question"
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.question}
						/>
						{touched.question && errors.question ? (
							<div className='form-error'>{errors.question}</div>
						) : null}
					</div>
					<div>
						<label htmlFor="category">Video Title:</label>
						<select
							id="video_id"
							name="video_id"
							onChange={handleChange}
							onBlur={handleBlur}
							value={values.video_id}
							className="select-dropdown"
						>
							<option value="" disabled>
								Select category
							</option>
							{videos?.length > 0 && videos.map((video) => (
								<option key={video.id} value={video.id}>
									{video.title}
								</option>
							))}
						</select>
						{touched.video_id && errors.video_id ? (
							<div className='form-error'>{errors.video_id}</div>
						) : null}
					</div>
					{values.options.map((option, index) => (
						<div key={option.id}>
							<label htmlFor={`options[${index}].option`}>{`Option ${option.id}`}</label>
							<input
								type="text"
								id={`options[${index}].option`}
								name={`options[${index}].option`}
								onChange={handleChange}
								onBlur={handleBlur}
								value={values.options[index].option}
							/>
							{touched.options && touched.options[index] && errors.options && errors.options[index] && (
								<div className="form-error">{errors.options[index].option}</div>
							)}
							<label htmlFor={`options[${index}].isCorrect`}>
								<input
									type="checkbox"
									id={`options[${index}].isCorrect`}
									name={`options[${index}].isCorrect`}
									onChange={handleChange}
									onBlur={handleBlur}
									checked={values.options[index].isCorrect}
								/>
								Correct
							</label>
							{touched.options && touched.options[index] && errors.options && errors.options[index] && (
								<div className="form-error">{errors.options[index].isCorrect}</div>
							)}
						</div>
					))}
					<div className='form-footer'>
						<button type='close' onClick={handleModal}>
							Close
						</button>
						<button type='submit' disabled={addLoading || editLoading}>
							Submit
						</button>
					</div>

				</form>
			</Modal>

		</div>
	)
}

export default QuizzForm